import React, { memo, useEffect, useRef } from "react";
import { Flex } from "@mantine/core";
import * as d3 from "d3";
import colorsCustom from "../designTokens/colorsCustom";

const CurvingSentences = memo(({ sentences = [], isBackground = true }) => {
	const containerRef = useRef();
	const svgRef = useRef();

	useEffect(() => {
		if (!sentences || sentences.length === 0) return;

		// LoginTopSection 애니메이션을 고려한 초기 딜레이 (약 2초)
		const initialDelay = 1200;
		
		// 표시할 최대 문장 수 제한 (10개)
		const maxSentences = Math.min(sentences.length, 10);
		const displaySentences = sentences.slice(0, maxSentences);

		// 뷰포트 크기 가져오기
		const viewportWidth = window.innerWidth;
		const viewportHeight = window.innerHeight;

		// SVG 요소 설정
		const svg = d3.select(svgRef.current)
			.attr("width", "100%")
			.attr("height", "100%")
			.attr("viewBox", `0 0 ${viewportWidth} ${viewportHeight}`)
			.style("position", "absolute")
			.style("top", 0)
			.style("left", 0)
			.style("pointer-events", "none");

		// 기존 요소 제거
		svg.selectAll("*").remove();

		// defs 영역 생성 (패스 정의용)
		const defs = svg.append("defs");

		// 각 문장마다 랜덤 패스와 텍스트 생성
		displaySentences.forEach((sentence, index) => {
			// 패스 ID 생성
			const pathId = `textPath-${index}`;
			
			// 패스 생성 로직 분기
			let pathData;
			let isLeftToRight;
			
			// 첫 8개 패스는 모서리 관련 패스
			if (index < 8) {
				// 패스 타입 결정
				let pathType, cornerFrom, cornerTo;
				
				if (index < 4) {
					// 첫 4개: 모서리에서 중앙으로 향하는 패스
					pathType = 'corner-to-center';
					cornerFrom = index;
					cornerTo = -1; // 중앙으로 (-1은 중앙을 의미)
				} else {
					// 다음 4개: 모서리에서 다른 모서리로 향하는 패스
					pathType = 'corner-to-corner';
					cornerFrom = index - 4;
					// 다른 모서리로 (마주보는 대각선 모서리로)
					cornerTo = (index + 2) % 4;
				}
				
				// 각 모서리 위치 계산 (0=왼쪽 상단, 1=오른쪽 상단, 2=오른쪽 하단, 3=왼쪽 하단)
				const fromVPos = Math.floor(cornerFrom / 2); // 상하 위치 (0 = 상단, 1 = 하단)
				const fromHPos = cornerFrom % 2;             // 좌우 위치 (0 = 좌측, 1 = 우측)
				
				// 모서리 패스 생성
				const cornerPaths = createCornerPath(
					viewportWidth, 
					viewportHeight,
					fromVPos,
					fromHPos,
					pathType,
					cornerTo
				);
				
				pathData = cornerPaths.pathData;
				isLeftToRight = cornerPaths.isLeftToRight;
			} 
			// 나머지 패스는 좌우 양방향 움직임
			else {
				// 균등 분포를 위한 화면 분할
				const totalRows = 2; // 나머지 패스들을 2줄로 배치
				const rowIndex = (index - 8) % totalRows; // 0, 1로 순환
				const colIndex = Math.floor((index - 8) / totalRows); // 컬럼 위치
				
				// 방향 설정 (열마다 번갈아가며)
				isLeftToRight = colIndex % 2 === 0;
				
				// 화면 바깥에서 시작/종료
				const startX = isLeftToRight ? -400 : viewportWidth + 400;
				const endX = isLeftToRight ? viewportWidth + 400 : -400;
				
				// 화면 높이를 균등하게 분할
				const rowHeight = viewportHeight / totalRows;
				const baseY = rowHeight * rowIndex + rowHeight * 0.5;
				
				// 굴곡 강화 - 크고 단순한 굴곡
				const yVariation = viewportHeight * 0.35; 
				
				// 단순한 굴곡 패스를 위해 제어점 감소
				const numPoints = 5; 
				const points = [];
				
				for (let i = 0; i < numPoints; i++) {
					// X 좌표는 시작부터 끝까지 균등하게 분포
					const xPos = startX + ((endX - startX) / (numPoints - 1)) * i;
					
					// 단순한 S자형 굴곡 생성
					let yPos;
					
					if (i === 0 || i === numPoints - 1) {
						// 시작과 끝은 기본선에 가깝게
						yPos = baseY;
					} else if (i === Math.floor(numPoints / 2)) {
						// 중간 지점은 최대 굴곡
						const direction = rowIndex % 2 === 0 ? 1 : -1; // 번갈아가며 위/아래로 굴곡
						yPos = baseY + (direction * yVariation);
					} else {
						// 중간 지점들은 부분 굴곡
						const normalizedPos = i / (numPoints - 1);
						const peakFactor = 1 - Math.abs(normalizedPos - 0.5) * 2;
						const direction = rowIndex % 2 === 0 ? 1 : -1;
						yPos = baseY + (direction * peakFactor * yVariation);
					}
					
					points.push({ x: xPos, y: yPos });
				}
				
				// 곡선 패스 생성 (d3.line 사용) - 더 부드러운 곡선
				const line = d3.line()
					.x(d => d.x)
					.y(d => d.y)
					.curve(d3.curveCatmullRom.alpha(0.5)); // 부드러운 곡선
				
				pathData = line(points);
			}
			
			// defs에 패스 추가
			defs.append("path")
				.attr("id", pathId)
				.attr("d", pathData);
			
			// 텍스트 스타일 설정 - 더 크고 볼드하게
			const fontSize = Math.floor(Math.random() * 24) + 32;
			const fontWeight = 900;
			const opacity = 0; // 처음에는 투명하게 시작 (fade-in 효과를 위해)
			
			// 텍스트 요소 생성 및 패스에 연결
			const textElement = svg.append("text")
				.attr("font-size", `${fontSize}px`)
				.attr("fill", colorsCustom.ui.dark.subtle)
				.attr("fill-opacity", opacity)
				.attr("font-weight", fontWeight)
				.attr("dominant-baseline", "middle")
				.style("pointer-events", "none");
			
			// 텍스트를 패스에 붙이기
			textElement.append("textPath")
				.attr("href", `#${pathId}`)
				.attr("startOffset", isLeftToRight ? "0%" : "100%")
				.text(sentence);
			
			// 애니메이션 적용 - 초기 딜레이 적용
			setTimeout(() => {
				// 페이드인 효과 (opacity 0 -> 0.5)
				textElement.transition()
					.duration(600)
					.ease(d3.easeCubicOut)
					.attr("fill-opacity", Math.random() * 0.4);
				
				// 패스 애니메이션 시작
				animateText(textElement, isLeftToRight, viewportWidth);
			}, initialDelay + (index * 150));
		});
		
		// 모서리 기반 패스 생성 함수
		function createCornerPath(width, height, vPos, hPos, pathType, toCorner) {
			// 패스 위치 결정 (0=상단, 1=하단) (0=좌측, 1=우측)
			const isTop = vPos === 0;
			const isLeft = hPos === 0;
			
			// 방향 결정 (랜덤 방향)
			const isLeftToRight = pathType === 'corner-to-center' ? true : Math.random() > 0.5;
			
			// 화면 분할 - 각 코너는 화면의 1/4 영역을 차지
			const sectionWidth = width / 2;
			const sectionHeight = height / 2;
			
			// 시작점 (모서리 위치)
			const startX = isLeft ? 0 : width;
			const startY = isTop ? 0 : height;
			
			// 종료점 (패스 타입에 따라 다름)
			let endX, endY;
			
			if (pathType === 'corner-to-center') {
				// 중앙으로 향하는 패스
				endX = width / 2;
				endY = height / 2;
			} else {
				// 다른 모서리로 향하는 패스
				const toIsTop = Math.floor(toCorner / 2) === 0;
				const toIsLeft = toCorner % 2 === 0;
				endX = toIsLeft ? 0 : width;
				endY = toIsTop ? 0 : height;
			}
			
			// 굴곡 강도
			const curveIntensity = Math.min(width, height) * 0.3;
			
			// 제어점들
			const points = [];
			
			// 시작점
			points.push({ x: startX, y: startY });
			
			if (pathType === 'corner-to-center') {
				// 모서리에서 중앙으로 향하는 패스
				
				// 첫 번째 제어점 (코너에서 약간 떨어진 위치)
				points.push({
					x: isLeft ? startX + sectionWidth * 0.2 : startX - sectionWidth * 0.2,
					y: isTop ? startY + sectionHeight * 0.2 : startY - sectionHeight * 0.2
				});
				
				// 두 번째 제어점 (큰 굴곡)
				points.push({
					x: isLeft ? startX + sectionWidth * 0.5 : startX - sectionWidth * 0.5,
					y: isTop ? startY + sectionHeight * 0.7 : startY - sectionHeight * 0.7
				});
				
				// 세 번째 제어점 (화면 중앙으로 향하는)
				points.push({
					x: endX + (isLeft ? -sectionWidth * 0.2 : sectionWidth * 0.2),
					y: endY + (isTop ? -sectionHeight * 0.2 : sectionHeight * 0.2)
				});
			} else {
				// 모서리에서 다른 모서리로 향하는 패스
				
				// 화면 중앙 지점 계산
				const centerX = width / 2;
				const centerY = height / 2;
				
				// 첫 번째 제어점 (코너에서 출발)
				points.push({
					x: isLeft ? startX + sectionWidth * 0.3 : startX - sectionWidth * 0.3,
					y: isTop ? startY + sectionHeight * 0.3 : startY - sectionHeight * 0.3
				});
				
				// 두 번째 제어점 (중앙 근처에서 큰 굴곡)
				points.push({
					x: centerX + (Math.random() * width * 0.2 - width * 0.1),
					y: centerY + (Math.random() * height * 0.2 - height * 0.1)
				});
				
				// 세 번째 제어점 (목표 모서리 근처)
				const toIsTop = Math.floor(toCorner / 2) === 0;
				const toIsLeft = toCorner % 2 === 0;
				points.push({
					x: toIsLeft ? endX + sectionWidth * 0.3 : endX - sectionWidth * 0.3,
					y: toIsTop ? endY + sectionHeight * 0.3 : endY - sectionHeight * 0.3
				});
			}
			
			// 종료점
			points.push({ x: endX, y: endY });
			
			// 곡선 패스 생성
			const line = d3.line()
				.x(d => d.x)
				.y(d => d.y)
				.curve(d3.curveCatmullRom.alpha(0.6));
			
			const pathData = line(points);
			
			return { pathData, isLeftToRight };
		}
		
		// 텍스트 애니메이션 함수
		function animateText(textElement, initialDirection, width) {
			// 현재 방향 (초기 방향으로 시작)
			let isLeftToRight = initialDirection;
			
			// 시작과 끝 위치 설정
			const startOffset = isLeftToRight ? "0%" : "100%";
			const endOffset = isLeftToRight ? "100%" : "0%";
			
			// 더 긴 애니메이션 지속 시간
			const duration = Math.random() * 15000 + 20000; // 30-55초
			
			// 시작 위치 설정
			const textPath = textElement.select("textPath")
				.attr("startOffset", startOffset);
				
			// 현재 투명도 저장용 변수
			let currentOpacity =  + Math.random() * 0.2; // 0.2-0.4 사이
			
			// 초기 페이드 인 (이미 적용되어 있으나 여기서 현재 투명도 설정)
			textElement.attr("fill-opacity", currentOpacity);
			
			// 애니메이션 함수 (반복)
			function animate() {
				// 먼저 경로를 따라 이동
				textPath.transition()
					.duration(duration)
					.ease(d3.easeLinear)
					.attr("startOffset", endOffset)
					.on("end", () => {
						// 경로 끝에 도달하면 페이드 아웃
						textElement.transition()
							.duration(600) // 페이드 아웃 시간
							.ease(d3.easeCubicIn) // 부드러운 페이드 아웃을 위한 ease 함수
							.attr("fill-opacity", 0) // 완전히 투명하게
							.on("end", () => {
								// 방향 전환 (양방향 왕복 움직임)
								isLeftToRight = !isLeftToRight;
								
								// 새로운 시작/종료 위치 설정
								const newStartOffset = isLeftToRight ? "0%" : "100%";
								const newEndOffset = isLeftToRight ? "100%" : "0%";
								
								// 즉시 시작 위치로 이동 (애니메이션 없이)
								textPath.attr("startOffset", newStartOffset);
								
								// 다시 페이드 인
								textElement.transition()
									.duration(800) // 페이드 인 시간
									.ease(d3.easeCubicOut) // 부드러운 페이드 인을 위한 ease 함수
									.attr("fill-opacity", currentOpacity) // 원래 투명도로 복원
									.on("end", () => {
										// 다음 애니메이션에서 새 종료 위치로 이동
										textPath.transition()
											.duration(duration)
											.ease(d3.easeLinear)
											.attr("startOffset", newEndOffset)
											.on("end", () => {
												// 다시 페이드 아웃 및 반복
												animate();
											});
									});
							});
					});
			}
			
			// 애니메이션 시작
			animate();
		}
		
		// 창 크기 변경 이벤트 리스너
		const handleResize = () => {
			// 일정 시간 후 새로고침 (디바운싱)
			if (window.resizeTimeout) {
				clearTimeout(window.resizeTimeout);
			}
			
			window.resizeTimeout = setTimeout(() => {
				svg.selectAll("*").remove();
				// 컴포넌트를 다시 마운트하는 대신 변수만 업데이트하고 리렌더링
				svg
					.attr("viewBox", `0 0 ${window.innerWidth} ${window.innerHeight}`);
				
				// 재귀적으로 동일한 로직 실행 (useEffect 재실행하지 않고)
				// 실제 구현에서는 여기에 위의 로직을 다시 작성해야 하지만,
				// 코드 중복을 피하기 위해 컴포넌트를 리렌더링하는 방식이 더 좋을 수 있음
			}, 250);
		};
		
		// 리사이즈 이벤트 리스너 등록
		window.addEventListener("resize", handleResize);
		
		// 클린업 함수
		return () => {
			window.removeEventListener("resize", handleResize);
			if (window.resizeTimeout) {
				clearTimeout(window.resizeTimeout);
			}
			svg.selectAll("*").remove();
		};
	}, [sentences]);

	return (
		<Flex
			direction={"column"}
			style={{
				overflow: "hidden",
				height: isBackground ? "100%" : "100vh",
				width: "100%",
				position: isBackground ? "absolute" : "fixed",
				top: 0,
				left: 0,
				zIndex: isBackground ? -1 : -1, // 항상 콘텐츠 뒤에 오도록 -1로 통일
				pointerEvents: "none",
			}}
			ref={containerRef}
		>
			<svg ref={svgRef} width="100%" height="100%"></svg>
		</Flex>
	);
});

export default CurvingSentences;
